<script setup lang="ts">
import rvapi, { type NewsletterContact } from "@/rvapi";
import { useAuthStore } from "@/stores/auth";
import { inXDays, utc2local } from "@/utils";
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
const auth = useAuthStore();
const { role } = storeToRefs(auth);
const searching = ref(false);
const generating = ref(false);
const emailResult = ref("");
const statusResult = ref<NewsletterContact | null>(null);
const error = ref("");
const unsubscribed = ref<NewsletterContact[]>([]);
const link = ref("");
const days = ref(30);
const selectChange = (e: { detail: string }) => {
  days.value = parseInt(e.detail);
};

onMounted(async () => {
  unsubscribed.value = await rvapi.searchGenericLookup(
    "newsletter/subscriptionStatus",
    "UNSUBSCRIBED",
    100,
    0,
  );
});
const exportList = async () => {
  generating.value = true;
  const date = inXDays(-1 * days.value);
  link.value = await rvapi.exportNewsletter(date + "T00:00:00Z");
  generating.value = false;
};
const unsub = async () => {
  searching.value = true;
  await rvapi.unsubscribeEmail(emailResult.value);
  await onSearch({ detail: emailResult.value });
};
const onSearch = async (e: any) => {
  if (!e.detail) {
    return;
  }
  const query = e.detail?.trim().toLowerCase();
  // test if query looks like email
  if (!/^\S+@\S+\.\S+$/.test(query)) {
    error.value = "This does not look like an email";
    return;
  } else {
    error.value = "";
  }
  searching.value = true;
  const result = await rvapi.getNewsletterInfo(query);
  searching.value = false;
  if (!result) {
    error.value = "This does not look like an email";
    return;
  }

  emailResult.value = query;
  statusResult.value = result?.contact;
};
</script>

<template>
  <section>
    <pn-toast closable="false">
      Migration of the newsletter list from sendgrid to pardot is ongoing,
      spring 2024. In some cases not all subscribers will get the emails.
    </pn-toast>
  </section>
  <section v-show="role == 'newsletter' || role == 'dev'">
    <h2>Export emails</h2>
    <p>Use this to generate a list of all subscribes and resubscribes</p>

    <p v-show="!link">
      <pn-select
        @selectchange="selectChange"
        label="Export N days of history"
        placeholder="30"
      >
        <option
          v-for="c in [30, 60, 90]"
          :key="c"
          :selected="c === days"
          :value="c"
        >
          {{ c }}
        </option>
      </pn-select>
    </p>
    <p>
      <pn-button v-show="!link" @click="exportList" :loading="generating">
        Generate csv
      </pn-button>
      <a v-show="link" :href="link">Here is your download link</a>
    </p>
  </section>
  <section v-if="role">
    <h2>Newsletter email search</h2>
    <pn-toast
      v-show="error"
      closable="false"
      appearance="error"
      :text="error"
    ></pn-toast>
    <div class="search">
      <pn-search-field
        name="query"
        v-on:search="onSearch"
        label=""
        :loading="searching"
      >
      </pn-search-field>
    </div>
  </section>

  <section v-show="role && !searching && emailResult">
    <dl>
      <dt>{{ emailResult }}</dt>
      <dd>{{ statusResult?.subscriptionStatus || "NOT_SUBSCRIBED" }}</dd>
      <dd>last updated: {{ utc2local(statusResult?.lastUpdated) }}</dd>
    </dl>
    <p v-show="!statusResult?.subscriptionStatus">
      "not subscribed" means we do not have this email in our newsletter system
      at all.
    </p>
    <p v-show="statusResult?.subscriptionStatus === 'SUBSCRIBED'">
      "subscribed" means this email will receive newsletters
    </p>
    <p v-show="statusResult?.subscriptionStatus === 'UNSUBSCRIBED'">
      "unsubscribed" means this user was subscribed in the past, but no longer
    </p>
    <!--p v-show="statusResult === 'NOT_SUBSCRIBED'">
      <pn-button @click="unsub" appearance="warning">
        Unsubscribe this email from newsletter
      </pn-button>
    </p-->
  </section>
  <section v-show="unsubscribed.length">
    <h3>Latest unsubscribes</h3>
    <pn-table bordered="true">
      <table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Date</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in unsubscribed" :key="u.email">
            <td>{{ u.email }}</td>
            <td>{{ utc2local(u.lastUpdated) }}</td>
            <td>{{ u.subscriptionStatus }}</td>
          </tr>
        </tbody>
      </table>
    </pn-table>
  </section>
</template>
<style lang="scss" scoped>
pn-search-field {
  width: 550px;
}
</style>
